import React, { useState, useEffect, useRef, useCallback } from "react";
import BackToTop from "../components/BackToTop";

// ============================================================================
// iamgod.co.za - Domain + Site Sale Landing Page
// Mobile-first, dark cinematic, glass morphism, particle effects
// ============================================================================

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.codeboothadmin.co.za/v1";

// ── Background Components ──

const DotGrid = ({ opacity = 0.4, size = 28 }) => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      opacity,
      backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
    }}
  />
);

const MeshGradient = ({ intensity = 1 }) => (
  <>
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: intensity,
        background: `
          radial-gradient(at 20% 25%, rgba(74,158,245,0.2) 0px, transparent 50%),
          radial-gradient(at 80% 75%, rgba(164,71,245,0.14) 0px, transparent 50%),
          radial-gradient(at 50% 50%, rgba(93,233,184,0.08) 0px, transparent 60%)
        `,
        animation: "meshDrift 20s ease-in-out infinite",
      }}
    />
    <style>{`
      @keyframes meshDrift {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33% { transform: translate(1.5%, -1%) scale(1.03); }
        66% { transform: translate(-1%, 1.5%) scale(1.02); }
      }
    `}</style>
  </>
);

// floating particles canvas
const ParticleField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let particles = [];
    const count = window.innerWidth < 768 ? 30 : 60;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // init particles
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        // wrap around
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(74, 158, 245, ${p.alpha})`;
        ctx.fill();
      });

      // draw lines between close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(74, 158, 245, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{ opacity: 0.6 }}
    />
  );
};

// glass card style
const glassStyle = {
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.08) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 20px 40px -12px rgba(0,0,0,0.5), 0 8px 16px -8px rgba(0,0,0,0.3)",
};

// ── Scroll-triggered fade in ──
const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

// ── Form Input ──
const FormInput = ({ label, type = "text", name, value, onChange, placeholder, error }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-medium tracking-wider uppercase text-white/50">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`
        w-full px-4 py-3 sm:py-3.5 rounded-xl text-sm text-white placeholder-white/25
        bg-white/[0.04] border backdrop-blur-xl
        outline-none transition-all duration-300
        focus:bg-white/[0.07] focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/20
        ${error ? "border-red-400/60" : "border-white/[0.08]"}
      `}
      style={{ fontFamily: "inherit", fontSize: "16px" }}
    />
    {error && <span className="text-xs text-red-400">{error}</span>}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function IAmGodLanding() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", bid_zar: "", addons: [], notes: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [stats, setStats] = useState({ total_bids: 0, highest_bid: 0 });
  const formRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/salesite/stats`)
      .then((r) => r.json())
      .then((d) => { if (d.success && d.data) setStats(d.data); })
      .catch(() => {});
  }, [submitted]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const toggleAddon = useCallback((addon) => {
    setForm((prev) => ({
      ...prev,
      addons: prev.addons.includes(addon)
        ? prev.addons.filter((a) => a !== addon)
        : [...prev.addons, addon],
    }));
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email";
    if (!form.phone.trim()) errs.phone = "Required";
    else if (form.phone.replace(/\D/g, "").length < 8) errs.phone = "Invalid phone";
    if (!form.bid_zar) errs.bid_zar = "Required";
    else if (parseFloat(form.bid_zar) <= 0) errs.bid_zar = "Must be greater than 0";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/salesite/bid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          bid_zar: parseFloat(form.bid_zar),
          addons: form.addons.length > 0 ? form.addons.join(", ") : null,
          notes: form.notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setSubmitError(data.error || "Something went wrong");
    } catch {
      setSubmitError("Network error. Please try again.");
    }
    setSubmitting(false);
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{
        fontFamily: "'Comfortaa', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#0a0a0c",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Comfortaa:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes typewriter-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        /* mobile input zoom prevention */
        input, select, textarea { font-size: 16px !important; }
        
        /* smooth scroll */
        html { scroll-behavior: smooth; }
        
        /* scrollbar */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        /* number input arrows hide */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {/* ════════════════════════════════════════════════════════════════════
          HERO
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        <ParticleField />
        <MeshGradient intensity={1.2} />
        <DotGrid opacity={0.35} />

        {/* vignette */}
        <div
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{
            background: "radial-gradient(ellipse at center, transparent 20%, rgba(10,10,12,0.85) 75%)",
          }}
        />

        {/* animated center glow */}
        <div
          className="absolute pointer-events-none z-[2]"
          style={{
            width: "min(500px, 80vw)",
            height: "min(500px, 80vw)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -55%)",
            background: "radial-gradient(circle, rgba(74,158,245,0.12) 0%, rgba(164,71,245,0.06) 40%, transparent 60%)",
            animation: "glow-pulse 4s ease-in-out infinite",
          }}
        />

        <div className="relative z-10 text-center px-5 sm:px-6 max-w-4xl mx-auto w-full">
          {/* status pill */}
          <FadeIn delay={0.1}>
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-8 sm:mb-10 bg-white/[0.04] border border-white/[0.1]">
              <span className="relative flex h-2 w-2">
                <span
                  className="absolute inline-flex h-full w-full rounded-full bg-green-400"
                  style={{ animation: "pulse-ring 1.5s ease-out infinite" }}
                />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-[10px] sm:text-[11px] font-medium tracking-widest uppercase text-white/50">
                Domain + site for sale
              </span>
            </div>
          </FadeIn>

          {/* domain name - responsive sizing */}
          <FadeIn delay={0.2}>
            <h1 className="text-[3rem] sm:text-7xl md:text-8xl lg:text-9xl font-bold tracking-tight leading-[1.1] mb-8 sm:mb-10">
              <span className="text-white">iam</span>
              <span
                className="inline-block pb-3 sm:pb-4"
                style={{
                  background: "linear-gradient(135deg, #4A9EF5, #5DE9B8, #A447F5, #4A9EF5)",
                  backgroundSize: "200% 200%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "gradient-shift 6s ease infinite",
                  marginBottom: "-0.75rem",
                }}
              >
                god
              </span>
              <span className="text-white/30">.co.za</span>
            </h1>
          </FadeIn>

          {/* taglines - each on own line */}
          <FadeIn delay={0.35}>
            <div className="flex flex-col gap-2 sm:gap-3 mb-10 sm:mb-12 max-w-lg mx-auto">
              <p className="text-base sm:text-lg text-white/50 font-light leading-relaxed">
                A premium South African domain.
              </p>
              <p className="text-base sm:text-lg text-white/50 font-light leading-relaxed">
                Bold, memorable, impossible to forget.
              </p>
              <p className="text-base sm:text-lg text-white/40 font-light leading-relaxed">
                Own the name that commands attention.
              </p>
            </div>
          </FadeIn>

          {/* CTAs */}
          <FadeIn delay={0.5}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full">
              <button
                onClick={scrollToForm}
                className="group w-full sm:w-auto px-8 py-4 rounded-full font-medium text-sm text-white transition-all duration-300 inline-flex items-center justify-center gap-2 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #4A9EF5, #3B8ED9)",
                  boxShadow: "0 4px 24px rgba(74,158,245,0.3)",
                }}
              >
                Place a bid
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
              <a
                href="#showcase"
                className="w-full sm:w-auto px-8 py-4 rounded-full font-medium text-sm border border-white/15 text-white/80 hover:bg-white/[0.04] transition-all duration-300 text-center active:scale-95"
              >
                What's included
              </a>
            </div>
          </FadeIn>

          {/* live stats */}
          <FadeIn delay={0.65}>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-12 sm:mt-16 max-w-md mx-auto">
              {[
                { value: stats.total_bids || "0", label: "Bids", color: "#4A9EF5" },
                { value: stats.highest_bid ? `R${Number(stats.highest_bid).toLocaleString()}` : "-", label: "Top bid", color: "#5DE9B8" },
                { value: ".co.za", label: "Premium", color: "#A447F5" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden p-4 sm:p-5 text-center"
                  style={glassStyle}
                >
                  {/* top accent glow */}
                  <div
                    className="absolute top-0 left-4 right-4 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, ${s.color}50, transparent)` }}
                  />
                  <div
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-12 pointer-events-none"
                    style={{ background: `radial-gradient(ellipse, ${s.color}15, transparent 70%)` }}
                  />
                  <div className="relative z-10">
                    <div className="text-lg sm:text-xl font-bold text-white leading-tight" style={{ textShadow: `0 0 20px ${s.color}30` }}>{s.value}</div>
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/30 mt-1.5">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center p-1.5">
            <div
              className="w-1 h-2 rounded-full bg-white/30"
              style={{ animation: "float 2s ease-in-out infinite" }}
            />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SHOWCASE - what's included
          ════════════════════════════════════════════════════════════════════ */}
      <section id="showcase" className="relative py-20 sm:py-32 overflow-hidden">
        <DotGrid opacity={0.2} size={32} />

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6">
          <FadeIn>
            <div className="text-center mb-12 sm:mb-16">
              <span className="text-[10px] sm:text-[11px] font-medium tracking-[0.2em] uppercase text-blue-400/70 mb-3 sm:mb-4 block">
                What you get
              </span>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-light tracking-tight text-white mb-3 sm:mb-4">
                A turnkey digital{" "}
                <span className="text-blue-400">presence</span>
              </h2>
              <p className="text-white/35 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
                This isn't just a domain. It's a fully designed, production-ready
                site built with modern tech. Fork it, rebrand it, launch it.
              </p>
            </div>
          </FadeIn>

          {/* bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              {
                icon: "M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418",
                title: "Premium .co.za Domain",
                desc: "Bold three-word domain that sticks. Already configured, live, and ready to transfer.",
                color: "#4A9EF5",
              },
              {
                icon: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5",
                title: "React + Tailwind Site",
                desc: "Modern, responsive, mobile-first. Production-grade code with dark-mode-first design.",
                color: "#5DE9B8",
              },
              {
                icon: "M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.764m3.42 3.42a6.776 6.776 0 00-3.42-3.42",
                title: "Full Design System",
                desc: "Glass morphism, mesh gradients, animated particles, custom effects. The full aesthetic toolkit.",
                color: "#A447F5",
              },
              {
                icon: "M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z",
                title: "Firebase Hosted",
                desc: "Global CDN, auto SSL, instant deploys. Zero server management needed.",
                color: "#F59047",
              },
              {
                icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
                title: "Instant Transfer",
                desc: "Domain, codebase, and hosting credentials — all transferred within 24 hours.",
                color: "#F55947",
              },
              {
                icon: "M11.42 15.17l-5.658-5.67 1.343-1.35 4.316 4.33 9.266-9.29L22.03 4.53l-10.61 10.64z",
                title: "SEO Ready",
                desc: "Open Graph, Twitter cards, meta tags, sitemap, robots.txt. Share-ready from day one.",
                color: "#8EF547",
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={0.1 * i}>
                <div
                  className="relative rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.10] p-5 sm:p-7 group hover:bg-white/[0.07] transition-all duration-500 h-full"
                  style={glassStyle}
                >
                  {/* shimmer on hover */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{
                        background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.02) 45%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.02) 55%, transparent 60%)",
                        animation: "shimmer 3s ease-in-out infinite",
                      }}
                    />
                  </div>
                  {/* top accent line */}
                  <div
                    className="absolute top-0 left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `linear-gradient(90deg, transparent, ${card.color}40, transparent)` }}
                  />
                  <div className="relative z-10 text-center sm:text-left">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 mx-auto sm:mx-0"
                      style={{
                        background: `${card.color}10`,
                        border: `1px solid ${card.color}25`,
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={card.color} strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={card.icon} />
                      </svg>
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-white mb-1.5">{card.title}</h3>
                    <p className="text-xs sm:text-sm text-white/35 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          CUSTOM BUILD UPSELL
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6">
          <FadeIn>
            <div
              className="relative rounded-3xl overflow-hidden bg-white/[0.02] border border-white/[0.06] p-6 sm:p-10 md:p-12"
              style={glassStyle}
            >
              {/* gradient accent top */}
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, #A447F5, #4A9EF5, transparent)" }}
              />

              {/* glow */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at 50% 0%, rgba(164,71,245,0.06) 0%, transparent 50%)",
                }}
              />

              <div className="relative z-10 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 sm:mb-6 bg-purple-400/[0.08] border border-purple-400/20">
                  <svg className="w-3.5 h-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                  </svg>
                  <span className="text-[10px] sm:text-[11px] font-medium tracking-widest uppercase text-purple-300/80">
                    Optional add-on
                  </span>
                </div>

                <h3 className="text-xl sm:text-3xl md:text-4xl font-light tracking-tight text-white mb-3 sm:mb-4">
                  Want a complete website or{" "}
                  <span className="text-purple-400">system build?</span>
                </h3>

                <p className="text-sm sm:text-base text-white/40 leading-relaxed max-w-lg mx-auto mb-3">
                  The bid is for the domain and showcase site only.
                </p>
                <p className="text-sm sm:text-base text-white/40 leading-relaxed max-w-lg mx-auto mb-6 sm:mb-8">
                  Need a full booking system, SaaS platform, business portal, or custom
                  build on top of it? That's available at an additional fee.
                  Select your interests in the bid form below and we'll scope it out.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                  {["SaaS platform", "Booking system", "Business portal", "Portfolio", "CMS", "API backend", "Mobile app"].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium bg-white/[0.03] border border-white/[0.08] text-white/40"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          BID FORM
          ════════════════════════════════════════════════════════════════════ */}
      <section ref={formRef} className="relative py-20 sm:py-32 overflow-hidden">
        <MeshGradient intensity={0.8} />

        {/* center glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: "min(800px, 90vw)",
            height: "min(800px, 90vw)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, rgba(74,158,245,0.08) 0%, transparent 50%)",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* LEFT - copy (desktop only shows here, mobile it stacks above) */}
            <FadeIn>
              <div className="text-center lg:text-left lg:sticky lg:top-24">
                <span className="text-[10px] sm:text-[11px] font-medium tracking-[0.2em] uppercase text-blue-400/70 mb-3 sm:mb-4 block">
                  Make it yours
                </span>
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-light tracking-tight text-white mb-4">
                  Place your{" "}
                  <span className="text-blue-400">bid</span>
                </h2>
                <p className="text-white/35 text-sm sm:text-base leading-relaxed mb-6">
                  Submit your offer in ZAR. All bids are confidential.
                </p>

                {/* desktop-only trust signals */}
                <div className="hidden lg:flex flex-col gap-4 mt-8">
                  {[
                    { icon: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z", text: "Domain transferred within 24 hours" },
                    { icon: "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z", text: "Secure and confidential bidding" },
                    { icon: "M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155", text: "We'll respond within 24 hours" },
                    { icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z", text: "Custom builds available at additional fee" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 mt-0.5 flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                        </svg>
                      </div>
                      <span className="text-sm text-white/40">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* RIGHT - form card */}
            <FadeIn delay={0.15}>
              <div
                className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-white/[0.02] border border-white/[0.08] backdrop-blur-2xl p-5 sm:p-8"
                style={glassStyle}
              >
                {/* inner top glow */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "radial-gradient(ellipse at 50% 0%, rgba(74,158,245,0.06) 0%, transparent 40%)",
                  }}
                />

                {submitted ? (
                  <div className="relative z-10 text-center py-6 sm:py-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-green-400/10 border border-green-400/20 flex items-center justify-center mx-auto mb-5">
                      <svg className="w-7 h-7 sm:w-8 sm:h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">Bid received</h3>
                    <p className="text-white/45 text-sm leading-relaxed">
                      We'll review your offer and get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <div className="relative z-10 flex flex-col gap-4 sm:gap-5">
                    {/* 2-col name/email on desktop */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <FormInput
                        label="Full name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        error={errors.name}
                      />
                      <FormInput
                        label="Email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        error={errors.email}
                      />
                    </div>

                    {/* 2-col phone/bid on desktop */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <FormInput
                        label="Phone"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+27 82 123 4567"
                        error={errors.phone}
                      />
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-medium tracking-wider uppercase text-white/50">
                          Bid amount (ZAR)
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/25 text-sm font-medium">R</span>
                          <input
                            type="number"
                            name="bid_zar"
                            value={form.bid_zar}
                            onChange={handleChange}
                            placeholder="50,000"
                            min="0"
                            step="100"
                            className={`
                              w-full pl-8 pr-4 py-3 sm:py-3.5 rounded-xl text-sm text-white placeholder-white/25
                              bg-white/[0.04] border backdrop-blur-xl
                              outline-none transition-all duration-300
                              focus:bg-white/[0.07] focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/20
                              ${errors.bid_zar ? "border-red-400/60" : "border-white/[0.08]"}
                            `}
                            style={{ fontFamily: "inherit", fontSize: "16px" }}
                          />
                        </div>
                        {errors.bid_zar && <span className="text-xs text-red-400">{errors.bid_zar}</span>}
                      </div>
                    </div>

                    {/* add-on interest toggles */}
                    <div className="flex flex-col gap-2">
                      <label className="text-[11px] font-medium tracking-wider uppercase text-white/50">
                        Interested in a custom build? <span className="text-white/25">(optional)</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {["SaaS platform", "Booking system", "Business portal", "Portfolio", "CMS", "API backend", "Mobile app"].map((addon) => {
                          const active = form.addons.includes(addon);
                          return (
                            <button
                              key={addon}
                              type="button"
                              onClick={() => toggleAddon(addon)}
                              className={`
                                px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium
                                transition-all duration-300 active:scale-95
                                ${active
                                  ? "bg-purple-400/15 border border-purple-400/40 text-purple-300"
                                  : "bg-white/[0.03] border border-white/[0.08] text-white/35 hover:border-white/[0.15] hover:text-white/50"
                                }
                              `}
                            >
                              {active && (
                                <svg className="w-3 h-3 inline mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                </svg>
                              )}
                              {addon}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* notes */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-medium tracking-wider uppercase text-white/50">
                        Additional notes <span className="text-white/25">(optional)</span>
                      </label>
                      <textarea
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        placeholder="Tell us about your project idea, timeline, or any questions..."
                        rows={3}
                        className="
                          w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/25
                          bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl
                          outline-none transition-all duration-300 resize-none
                          focus:bg-white/[0.07] focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/20
                        "
                        style={{ fontFamily: "inherit", fontSize: "16px" }}
                      />
                    </div>

                    {submitError && (
                      <div className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                        {submitError}
                      </div>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="mt-1 w-full py-3.5 sm:py-4 rounded-xl font-medium text-sm text-white transition-all duration-300 disabled:opacity-50 active:scale-[0.98]"
                      style={{
                        background: submitting
                          ? "rgba(74,158,245,0.3)"
                          : "linear-gradient(135deg, #4A9EF5, #3B8ED9)",
                        boxShadow: submitting ? "none" : "0 4px 24px rgba(74,158,245,0.25)",
                      }}
                    >
                      {submitting ? "Submitting..." : "Submit bid"}
                    </button>

                    <p className="text-[10px] sm:text-[11px] text-white/25 text-center mt-0.5">
                      Your information is kept confidential. Bidding is for the domain and showcase site only.
                    </p>
                  </div>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════════════════════════════════ */}
      <footer className="relative border-t border-white/[0.04] py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 flex items-center justify-center">
          <div className="text-[11px] text-white/15">
            iamgod.co.za &mdash; Premium domain listing
          </div>
        </div>
      </footer>

      <BackToTop />
    </div>
  );
}